import React, { useState, useEffect } from 'react';
import constants from '../constants/constants';

const TIMEOUT_TIME = 10000;

const BREAD_EVENTS = {
  DECISIONED: 'INSTALLMENT:APPLICATION_DECISIONED',
  CHECKOUT: 'INSTALLMENT:APPLICATION_CHECKOUT',
  OPEN: 'INSTALLMENT:CUSTOMER_OPEN',
  CLOSE: 'INSTALLMENT:CUSTOMER_CLOSE',
  INITIALIZED: 'INSTALLMENT:INITIALIZED'
};

declare global {
  // eslint-disable-next-line no-unused-vars
  interface Window {
    BreadPayments: any;
  }
}

export default function useBread(
  linkWrapperRef: React.RefObject<HTMLInputElement>,
  customPrice?: number
) {
  const [breadLoaded, setBreadLoaded] = useState(false);
  const [breadError, setBreadError] = useState(false);
  const customTotal = (customPrice || constants.bikePrice) * 100;
  const customTotalNumber = Number(customTotal);
  let placements: Array<any> = [];

  const showError = () => {
    setBreadError(true);
    window.location.hash = '';
    window.history.replaceState('', '', window.location.pathname);
  };

  const hideError = () => {
    setBreadError(false);
  };

  const blankMethod = () => {};

  // Timeout that verifies if modal opens
  // The modal is an iframe, if it has content it will break when we try to access its content
  // If it throws error accessing the content it means it did load the data.
  const detectError = () => {
    setTimeout(() => {
      const iframe = <HTMLObjectElement>document.getElementById('bread-modal');
      if (!iframe) {
        showError();
        return;
      }
      try {
        const iframeDoc =
          iframe.contentDocument || iframe?.contentWindow?.document;
        if (iframeDoc) {
          showError();
        }
      } catch (error) {
        hideError();
      }
    }, TIMEOUT_TIME);
  };

  const handleBreadModalOpen = () => {
    // check for errors that bread does not handle
    detectError();
    if (typeof window.BreadPayments !== 'undefined') {
      window.BreadPayments.openExperienceForPlacement(placements);
    } else {
      console.error('Something went wrong opening Bread modal.');
    }
  };

  const handleClick = () => {
    setTimeout(() => {
      if (window.location.hash === '#breadModal') {
        handleBreadModalOpen();
      }
    }, 200);
  };

  const setUpBreadListeners = () => {
    window.BreadPayments.on(BREAD_EVENTS.DECISIONED, blankMethod);
    window.BreadPayments.on(BREAD_EVENTS.CHECKOUT, blankMethod);
    window.BreadPayments.on(BREAD_EVENTS.INITIALIZED, blankMethod);
  };

  useEffect(() => {
    if (linkWrapperRef && linkWrapperRef.current) {
      linkWrapperRef.current.addEventListener('click', handleClick);
    }

    if (!window.BreadPayments) {
      return;
    }

    window.BreadPayments.setup({
      integrationKey: process.env.BREAD_INTEGRATION_KEY
    });

    window.BreadPayments.setInitMode('manual');

    placements = [
      {
        financingType: 'installment',
        locationType: 'product',
        domID: 'bread-checkout-btn',
        allowCheckout: false,
        order: {
          items: [
            {
              name: 'Soul Cycle Bike ',
              sku: '',
              category: '',
              unitPrice: {
                value: customTotalNumber,
                currency: 'USD'
              },
              unitTax: {
                value: 0,
                currency: 'USD'
              },
              brand: 'Soul Cycle',
              quantity: 1,
              shippingDescription: '',
              shippingProvider: '',
              shippingCost: {
                value: 0,
                currency: 'USD'
              }
            }
          ],
          subTotal: {
            value: customTotalNumber,
            currency: 'USD'
          },
          totalTax: {
            value: 0,
            currency: 'USD'
          },
          totalShipping: {
            value: 0,
            currency: 'USD'
          },
          totalDiscounts: {
            value: 0,
            currency: 'USD'
          },
          totalPrice: {
            value: customTotalNumber,
            currency: 'USD'
          }
        }
      }
    ];

    if (!window.BreadPayments?.options?.integrationKey) {
      return;
    }

    window.BreadPayments.registerPlacements(placements);

    setUpBreadListeners();

    window.BreadPayments.init();

    return () => {
      if (linkWrapperRef && linkWrapperRef.current) {
        linkWrapperRef.current.removeEventListener('click', handleClick);
      }
    };
  }, [breadLoaded]);

  useEffect(() => {
    const hasDocument = typeof document !== 'undefined';
    const hasBreadScript = document?.getElementById('breadScript');
    if (!hasDocument || hasBreadScript) {
      return;
    }
    const script = document.createElement('script');
    script.id = 'breadScript';
    script.src = process.env.BREAD_URL || '';
    script.onload = () => {
      setBreadLoaded(true);
    };
    document.body.appendChild(script);
  }, []);

  return {
    breadError,
    hideError
  };
}
